<template>
  <div v-if="isReadOnly" class="flex flex-col gap-x-2">
    <label v-if="label" :for="fieldName" class="">
      <span class="text-brand-black-secondary">{{ t9nLabel }}</span>
      <HintTippy v-if="hint" :hint="hint"></HintTippy>
    </label>
    <span class="flex-1 text-lg">
      {{ displayLabel?.[optionValue] }}
    </span>
  </div>
  <div v-else class="flex flex-col gap-x-4">
    <label class="w-full flex flex-col gap-0.5">
      <div
        :class="direction"
        class="w-full flex flex-wrap gap-x-4 gap-y-1 text-brand-black-secondary"
        ref="wrapperDomRef"
      >
        <div v-if="label" :class="labelClass" class="flex w-1/2 items-center">
          {{ label }} <span v-if="isRequired" class="text-brand-red">*</span>
        </div>
        <tippy
          :offset="[0, 0]"
          :arrow="false"
          theme="light-border"
          trigger="focus click"
          interactive
          placement="bottom-start"
          class="w-full flex items-center"
          @mount="setTippyWidth"
        >
          <button
            :disabled="disabled"
            :class="[
              inputClass,
              { 'border-s-4  border-s-primary': isRequired, 'bg-brand-gray-250': disabled },
              disabled ? 'cursor-not-allowed' : ''
            ]"
            class="w-full px-3 py-1.5 flex justify-between items-center border-s-4 border-s-primary rounded-sm border border-brand-grayblue-300 outline-none focus:border-primary text-xs text-start truncate line-clamp-1"
            @click.prevent=""
          >
            <span v-if="!field.value" class="text-brand-gray-600">
              {{ t9nPlaceholder || t9nLabel }}
            </span>
            <span v-else class="truncate">
              {{ displayLabel?.[optionValue] }}
            </span>
            <div class="flex gap-x-3">
              <button
                v-if="field.value && !disabled"
                @click.stop.prevent="$emit('onSelection', null)"
              >
                <faTimes class="w-3 fill-brand-red" />
              </button>
              <faChevronDown v-if="!disabled" class="w-4" />
            </div>
          </button>
          <template #content="{ hide }">
            <div class="tippy-content__container w-full" :style="{ width: wrapperWidth }">
              <div class="w-full flex flex-col gap-y-1">
                <div class="px-1.5 pt-1">
                  <input
                    v-model="searchValue"
                    type="text"
                    class="w-full ps-3 py-1.5 rounded-sm border border-brand-grayblue-300 outline-none focus:border-primary"
                  />
                </div>
                <div class="max-h-[12rem] h-full overflow-y-auto">
                  <ul class="divide-y pb-2">
                    <li v-for="(option, key) in filteredOptions" :key="key" class="">
                      <button
                        class="ps-3 py-1.5 hover:bg-gray-100 w-full text-start"
                        @click.prevent="
                          () => {
                            hide()
                            $emit('onSelection', option)
                          }
                        "
                      >
                        {{ option[optionValue] }}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </template>
        </tippy>
      </div>
    </label>
    <div class="flex flex-col gap-y-1 text-xs ltr:font-sans">
      <span :key="err" v-for="err in errors" class="text-brand-red">
        {{ $t(err) }}
      </span>
    </div>
  </div>
</template>

<script setup>
import faChevronDown from '@/assets/icons/faChevronDown.svg?component'
import faTimes from '@/assets/icons/faTimes.svg?component'
import HintTippy from '@/components/Form/HintTyppy.vue'
import { useDebounceFn, useEventListener } from '@vueuse/core'
import Fuse from 'fuse.js'
import { computed, onBeforeUnmount, onMounted, ref, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()

const props = defineProps({
  label: String,
  field: Object,
  errors: Array,
  name: String,
  mask: String,
  placeholder: String,
  options: {
    type: Array,
    required: true
  },
  inputClass: {
    type: String,
    default: () => ''
  },
  optionValue: {
    type: String,
    default: 'label'
  },
  fuzzyKeywords: {
    type: Array,
    default: () => ['label']
  },
  labelClass: String,
  disabled: Boolean,
  direction: {
    type: String,
    default: 'flex-row md:items-center'
  },
  isRequired: {
    type: Boolean,
    default: () => true
  },
  isReadOnly: Boolean,
  fuseOptions: {
    type: Object,
    default: () => ({
      threshold: 0.2
    })
  }
})

const t9nLabel = computed(() => i18n.t(props.label || ''))
const t9nPlaceholder = computed(() => i18n.t(props.placeholder ?? 'pleaseSelect'))

const searchValue = ref()
const wrapperWidth = ref()
const wrapperDomRef = ref()
const fuse = ref()

onMounted(() => {
  fuse.value = new Fuse(props.options, {
    includeScore: true,
    keys: props.fuzzyKeywords,
    ...props.fuseOptions
  })
})

const displayLabel = computed(() => {
  if (!props.field.value) return
  return props.options.find(
    (fo) =>
      fo.code == props.field.value || fo.id == props.field.value || fo.value == props.field.value
  )
})

onBeforeUnmount(() => {
  cleanupResize()
})

const cleanupResize = useEventListener(
  window,
  'resize',
  useDebounceFn((e) => {
    setTippyWidth()
  }, 1000)
)

const filteredOptions = computed(() => {
  if (!searchValue.value) return props.options
  const result = fuse.value.search(searchValue.value)
  return result.map((r) => r.item)
})

const setTippyWidth = (instance) => {
  nextTick(() => {
    if (instance) {
      const itemDom = document
        .getElementById(`tippy-${instance.id}`)
        .getElementsByClassName('tippy-box')[0]
      itemDom.style['max-width'] = wrapperDomRef.value.offsetWidth + 'px'
      itemDom.style['width'] = wrapperDomRef.value.offsetWidth + 'px'
    }
  })
}
</script>

<style lang="scss" scoped></style>
